import { useState, useEffect, useCallback } from 'react';

const THEME_KEY = 'theme';

// Module-level storage for current theme vars so dark/light toggle can re-apply
let _themeVars = null;
let _darkOnly = false;

function getSystemPreference() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveIsDark(theme) {
  if (_darkOnly) return true;
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return getSystemPreference();
}

function applyDarkClass(isDark) {
  document.documentElement.classList.toggle('dark', isDark);
}

export function applyCssVars(themeVars, darkOnly) {
  if (themeVars) _themeVars = themeVars;
  if (darkOnly !== undefined) _darkOnly = darkOnly;

  const style = document.documentElement.style;

  // Clear any previously applied theme color vars (both scale and semantic)
  const toRemove = [];
  for (let i = 0; i < style.length; i++) {
    const prop = style[i];
    if (prop.startsWith('--color-')) {
      toRemove.push(prop);
    }
  }
  toRemove.forEach((prop) => style.removeProperty(prop));

  if (!_themeVars) return;

  // Force dark mode for dark-only themes
  if (_darkOnly) applyDarkClass(true);

  // Pick the right set based on current dark/light mode
  const stored = localStorage.getItem(THEME_KEY) || 'light';
  const isDark = resolveIsDark(stored);
  const vars = isDark ? _themeVars.dark : _themeVars.light;

  if (vars && typeof vars === 'object') {
    Object.entries(vars).forEach(([key, value]) => {
      style.setProperty(key, value);
    });
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem(THEME_KEY) || 'light';
  });

  const isDark = resolveIsDark(theme);
  const darkOnly = _darkOnly;

  const setTheme = useCallback((newTheme) => {
    if (_darkOnly) return; // Prevent toggling when theme is dark-only
    setThemeState(newTheme);
    localStorage.setItem(THEME_KEY, newTheme);
    applyDarkClass(resolveIsDark(newTheme));
    // Re-apply the correct set of CSS vars for the new mode
    applyCssVars();
  }, []);

  // Apply on mount and listen for system preference changes when set to 'system'
  useEffect(() => {
    applyDarkClass(isDark);

    if (theme === 'system' && !_darkOnly) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e) => {
        applyDarkClass(e.matches);
        applyCssVars();
      };
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [theme, isDark]);

  return { theme, setTheme, isDark, darkOnly };
}

/**
 * Call at app startup (before React renders) to prevent flash.
 * themeVars: { light: { "--color-primary-500": "#...", ... }, dark: { ... } }
 */
export function initializeTheme(themeVars, darkOnly) {
  _themeVars = themeVars || null;
  _darkOnly = darkOnly || false;
  const stored = localStorage.getItem(THEME_KEY) || 'light';
  applyDarkClass(resolveIsDark(stored));
  applyCssVars();
}
